import { useCallback, useMemo } from 'react';
import { useCurrentUser } from '@bpm-web-app/swr-hooks';
import { useHubSwitch } from '@bpm-web-app/utils';
import classNames from 'classnames';
import styles from './platform-switch.module.css';
import CreateLogo from '../../../assets/icons/create/bpm-create-logo-icon.svg';
import SupremeLogo from '../../../assets/icons/bpm-supreme-logo-icon.svg';
import ArtistPlatformLogo from '../../../assets/icons/bpm-artist-platform-logo-icon.svg';
import CloseIcon from '../../../assets/icons/close.svg';
import { SHOW_WHITE_LABEL } from '../../library-tabs/library-tabs';

export interface PlatformSwitchProps {
    onClose: () => void;
    platforms: { name: 'BPM Supreme' | 'BPM Create' | 'BPM Artist' }[]
}

const linkDict = {
    links: {
        local: {
            create: 'https://local.bpmsupreme.com:4201/',
            supreme: 'https://local.bpmsupreme.com:4101/d',
            artist: 'https://local.bpmsupreme.com:4301'
        },
        dev: {
            create: 'https://develop.bpmcreate.com/',
            supreme: 'https://develop.bpmsupreme.com/',
            artist: 'https://develop.artist.bpmmusic.io/'
        },
        qa: {
            create: 'https://qa.bpmcreate.com/',
            supreme: 'https://qa.bpmsupreme.com/',
            artist: 'https://qa.artist.bpmmusic.io/'
        },
        prod: {
            create: 'https://app.bpmcreate.com/',
            supreme: 'https://app.bpmsupreme.com/',
            artist: 'https://artist.bpmmusic.io/'
        }
    }
};

export function PlatformSwitch({ onClose, platforms }: PlatformSwitchProps) {
    const { data } = useCurrentUser();
    const { isCreate } = useHubSwitch();

    const logoForPlatform = useCallback((platform: 'BPM Supreme' | 'BPM Create' | 'BPM Artist') => {
        switch (platform) {
            case 'BPM Create':
                return <CreateLogo />;
            case 'BPM Supreme':
                return <SupremeLogo />;
            default:
                return <ArtistPlatformLogo />;
        }
    }, []);

    const actualPlatforms = useMemo(() => platforms.filter(({ name }) => {
        if (name === 'BPM Artist') {
            if (SHOW_WHITE_LABEL || data?.data?.user?.beta_tester === true) {
                return true;
            }
            return false;
        }

        return true;
    }), [platforms, data]);

    const platformEnvironmentLink = useCallback((platform: 'BPM Supreme' | 'BPM Create' | 'BPM Artist') => {
        // eslint-disable-next-line no-restricted-globals
        const envData = location.host.split('.');
        const env = envData[0];

        switch (env) {
            case 'qa':
                return platform === 'BPM Create' ? linkDict.links.qa.create
                    : platform === 'BPM Supreme' ? linkDict.links.qa.supreme
                        : linkDict.links.qa.artist;
            case 'local':
                return platform === 'BPM Create' ? linkDict.links.local.create
                    : platform === 'BPM Supreme' ? linkDict.links.local.supreme
                        : linkDict.links.local.artist;
            case 'dev':
                return platform === 'BPM Create' ? linkDict.links.dev.create
                    : platform === 'BPM Supreme' ? linkDict.links.dev.supreme
                        : linkDict.links.dev.artist;
            default:
                return platform === 'BPM Create' ? linkDict.links.prod.create
                    : platform === 'BPM Supreme' ? linkDict.links.prod.supreme
                        : linkDict.links.prod.artist;
        }
    }, []);

    return (
        <div className={classNames(styles['platform-switch'], { [styles['platform-switch__create']]: isCreate })}>
            <div className={styles['platform-switch__header']}>
                <h3>Switch Platforms</h3>
                <button className={styles['platform-switch__button']} type="button" aria-label="Close" onClick={onClose}>
                    <CloseIcon />
                </button>
            </div>
            <div>
                {actualPlatforms.map((platform) => {
                    return (
                        <div className={styles['platform-switch__section']}>
                            <div className={styles['platform-switch__section-logo']}>
                                <div>
                                    {logoForPlatform(platform.name)}
                                </div>
                                <h4 className={styles['platform-switch__section-title']}>{platform.name}</h4>
                            </div>
                            <button className={styles['platform-switch__switch-btn']} title="Switch Platform" type="button"><a href={platformEnvironmentLink(platform.name)}><span>Switch Platform</span></a></button>
                        </div>);
                })}

            </div>
        </div>
    );
}
